import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../api/client";
import {
  BASE, toBs, ProductImg,
  PLANTILLAS, PlantillaClasica,
  FUENTES_URL,
} from "../components/Plantillas";

const publicApi = axios.create({ baseURL: BASE_URL });

const SIMBOLOS_MONEDA = {
  VES: "Bs.", COP: "$", MXN: "$", ARS: "$", CLP: "$",
  PEN: "S/", USD: "$", BOB: "Bs.", UYU: "$U", PYG: "₲",
  CRC: "₡", GTQ: "Q", HNL: "L", NIO: "C$", DOP: "RD$",
  CUP: "$", EUR: "€", PAB: "B/.",
};

const MONEDA_NOMBRES = {
  USD: "Dólares estadounidenses",
  EUR: "Euros",
  COP: "Pesos colombianos",
  MXN: "Pesos mexicanos",
  ARS: "Pesos argentinos",
  CLP: "Pesos chilenos",
  PEN: "Soles peruanos",
  BOB: "Bolivianos",
  UYU: "Pesos uruguayos",
  PYG: "Guaraníes",
  CRC: "Colones costarricenses",
  GTQ: "Quetzales",
  HNL: "Lempiras hondureños",
  NIO: "Córdobas nicaragüenses",
  DOP: "Pesos dominicanos",
  CUP: "Pesos cubanos",
  PAB: "Balboas panameños",
};

/* ─── Carrito ─────────────────────────────────────────────────────────────── */
function useCarrito() {
  const [items, setItems] = useState([]);
  const makeUid = (prod, variantes) => {
    const vStr = Object.keys(variantes || {}).sort().map(k => `${k}:${variantes[k]}`).join("|");
    return `${prod.id}|${vStr}`;
  };
  const agregar = (prod, variantes = {}) =>
    setItems((prev) => {
      const uid = makeUid(prod, variantes);
      const ex = prev.find((i) => i._uid === uid);
      return ex
        ? prev.map((i) => (i._uid === uid ? { ...i, qty: i.qty + 1 } : i))
        : [...prev, { ...prod, _uid: uid, variantes: variantes || {}, qty: 1 }];
    });
  const quitar = (uid) =>
    setItems((prev) => {
      const ex = prev.find((i) => i._uid === uid);
      if (!ex) return prev;
      return ex.qty === 1 ? prev.filter((i) => i._uid !== uid) : prev.map((i) => (i._uid === uid ? { ...i, qty: i.qty - 1 } : i));
    });
  const vaciar = () => setItems([]);
  const total = items.reduce((s, i) => s + Number(i.precio_promocion || i.precio) * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);
  return { items, agregar, quitar, vaciar, total, count };
}

/* ─── Drawer del carrito ──────────────────────────────────────────────────── */
function CartDrawer({ open, carrito, onClose, onCheckout, color, tasa, simbolo = "$" }) {
  if (!open) return null;
  const bs = tasa ? (carrito.total * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null;
  return (
    <>
      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes slideInUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .cart-panel {
          position: fixed; top: 0; right: 0; bottom: 0;
          width: min(420px, 100vw);
          background: #fff; z-index: 51;
          box-shadow: -8px 0 40px rgba(0,0,0,0.18);
          display: flex; flex-direction: column;
          animation: slideInRight 0.25s ease;
          overflow-x: hidden;
        }
        .cart-items { flex: 1; overflow-y: auto; padding: 12px 20px; }
        @media (max-width: 600px) {
          .cart-panel {
            top: auto; left: 0; right: 0; width: 100%;
            max-height: 85vh;
            border-radius: 24px 24px 0 0;
            box-shadow: 0 -8px 40px rgba(0,0,0,0.2);
            animation: slideInUp 0.25s ease;
          }
          .cart-items { flex: 0 0 auto; max-height: 52vh; }
        }
      `}</style>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 50, backdropFilter: "blur(2px)" }} />
      <div className="cart-panel">
        <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h2 style={{ fontWeight: 800, fontSize: 18, color: "#0f172a", margin: 0 }}>🛒 Tu pedido</h2>
            <p style={{ fontSize: 12, color: "#94a3b8", margin: "2px 0 0" }}>{carrito.count} {carrito.count === 1 ? "producto" : "productos"}</p>
          </div>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "#f1f5f9", cursor: "pointer", fontSize: 18, color: "#64748b", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
        <div className="cart-items">
          {carrito.items.length === 0 ? (
            <div style={{ textAlign: "center", paddingTop: 60, color: "#94a3b8" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🛍️</div>
              <p style={{ fontWeight: 600, fontSize: 15 }}>Tu carrito está vacío</p>
              <p style={{ fontSize: 13, marginTop: 4 }}>Agrega productos para comenzar</p>
            </div>
          ) : (
            carrito.items.map((item) => {
              const precioUnit = Number(item.precio_promocion || item.precio);
              const subtotal = precioUnit * item.qty;
              const bsUnit = tasa ? toBs(precioUnit, tasa) : null;
              const bsSubtotal = tasa ? (subtotal * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null;
              const varText = item.variantes && Object.keys(item.variantes).length > 0
                ? Object.entries(item.variantes).map(([k, v]) => `${k}: ${v}`).join(" · ") : null;
              return (
                <div key={item._uid} style={{ display: "flex", gap: 10, padding: "12px 0", borderBottom: "1px solid #f8fafc" }}>
                  <div style={{ width: 58, height: 58, borderRadius: 10, overflow: "hidden", flexShrink: 0, background: "#f1f5f9" }}>
                    <ProductImg url={item.foto_url} nombre={item.nombre} color={color} size={58} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                      <p style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", margin: 0, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.nombre}</p>
                      <span style={{ fontWeight: 800, fontSize: 14, color: "#0f172a", flexShrink: 0 }}>{simbolo}{subtotal.toFixed(2)}</span>
                    </div>
                    {varText && <p style={{ fontSize: 11, color, margin: "2px 0 0", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{varText}</p>}
                    <p style={{ fontSize: 11, color: "#94a3b8", margin: "2px 0 6px" }}>
                      {simbolo}{precioUnit.toFixed(2)} c/u{bsUnit && ` · Bs. ${bsUnit}`}
                    </p>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <button onClick={() => carrito.quitar(item._uid)} style={{ width: 26, height: 26, borderRadius: "50%", border: `1.5px solid ${color}`, background: "transparent", color, fontWeight: 900, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
                        <span style={{ fontWeight: 800, fontSize: 14, color: "#0f172a", minWidth: 18, textAlign: "center" }}>{item.qty}</span>
                        <button onClick={() => carrito.agregar(item, item.variantes)} style={{ width: 26, height: 26, borderRadius: "50%", border: "none", background: color, color: "#fff", fontWeight: 900, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
                      </div>
                      {bsSubtotal && <span style={{ fontSize: 11, color: "#94a3b8" }}>Bs. {bsSubtotal}</span>}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {carrito.items.length > 0 && (
          <div style={{ padding: "16px 20px 24px", borderTop: "1px solid #f1f5f9", background: "#fafbfc" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ color: "#64748b", fontSize: 14 }}>Subtotal</span>
              <span style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>{simbolo}{carrito.total.toFixed(2)}</span>
            </div>
            {bs && <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}><span style={{ fontSize: 12, color: "#94a3b8" }}>≈ Bs. {bs}</span></div>}
            <button onClick={onCheckout} style={{ width: "100%", background: color, color: "#fff", border: "none", borderRadius: 14, padding: "15px 0", fontWeight: 800, fontSize: 15, cursor: "pointer", boxShadow: `0 4px 20px ${color}55`, transition: "opacity 0.15s" }} onMouseOver={e => e.currentTarget.style.opacity = "0.9"} onMouseOut={e => e.currentTarget.style.opacity = "1"}>
              📱 Confirmar pedido por WhatsApp
            </button>
            <button onClick={carrito.vaciar} style={{ width: "100%", background: "transparent", border: "none", color: "#94a3b8", fontSize: 12, cursor: "pointer", marginTop: 10, padding: "4px 0" }}>Vaciar carrito</button>
          </div>
        )}
      </div>
    </>
  );
}

/* ─── Checkout Modal ──────────────────────────────────────────────────────── */
const LABELS_PAGO = {
  efectivo: "Efectivo", transferencia: "Transferencia bancaria", pago_movil: "Pago móvil",
  zelle: "Zelle", paypal: "PayPal", binance: "Binance Pay", tarjeta: "Tarjeta de crédito/débito",
};

function CheckoutModal({ open, carrito, subdominio, config, onClose, onSuccess, simbolo = "$", monedaLocal = "USD", tasa = null }) {
  const [form, setForm] = useState({ cliente_nombre: "", cliente_telefono: "", cliente_direccion: "", notas: "" });
  const [tipoEntrega, setTipoEntrega] = useState("delivery");
  const [formaPago, setFormaPago] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  // Para monedas con símbolo $ ambiguo (no Venezuela), prefija el código ISO: "COP$", "MXN$", etc.
  const monedaDisplay = (monedaLocal !== "VES" && simbolo === "$") ? `${monedaLocal}$` : simbolo;

  if (!open) return null;

  const metodosPago = config?.formas_pago
    ? Object.entries(config.formas_pago).filter(([, v]) => v).map(([k]) => ({ key: k, label: LABELS_PAGO[k] || k }))
    : [];

  const construirMensaje = (pedidoId) => {
    const e = String.fromCodePoint;
    const ICO = { carrito: e(0x1F6D2), num: e(0x1F522), caja: e(0x1F4E6), dinero: e(0x1F4B5), cambio: e(0x1F4B1), cliente: e(0x1F464), tel: e(0x1F4F1), pin: e(0x1F4CD), tarjeta: e(0x1F4B3), cal: e(0x1F4C5), reloj: e(0x23F0), nota: e(0x1F4DD) };
    const nombreTienda = config?.nombre_comercio || "";
    const lineas = carrito.items.map((i) => {
      const varText = i.variantes && Object.keys(i.variantes).length > 0
        ? ` (${Object.entries(i.variantes).map(([k, v]) => `${k}: ${v}`).join(", ")})` : "";
      return `  • ${i.qty}x ${i.nombre}${varText} — ${monedaDisplay}${(Number(i.precio_promocion || i.precio) * i.qty).toFixed(2)}`;
    }).join("\n");
    const entregaTexto = tipoEntrega === "delivery" ? `Delivery\n   ${ICO.pin} ${form.cliente_direccion || "Sin direccion"}` : "Retiro en tienda";
    const pagoLabel = (metodosPago.find((m) => m.key === formaPago) || {}).label || formaPago || "No indicado";
    const bsTotal = tasa ? `\n   ${ICO.cambio} Bs. ${(carrito.total * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
    const ahora = new Date();
    const fecha = ahora.toLocaleDateString("es-VE", { day: "numeric", month: "numeric", year: "numeric" });
    const hora = ahora.toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit", hour12: true });
    const notasLinea = form.notas ? `\n${ICO.nota} *Observaciones:* ${form.notas}` : "";
    const sep = "────────────────────";
    return [
      `${ICO.carrito} *${nombreTienda ? `Pedido en ${nombreTienda}` : "Nuevo pedido"}*`,
      `${ICO.num} *Pedido #${pedidoId}*`,
      sep,
      `${ICO.caja} *Productos:*`,
      lineas,
      sep,
      `${ICO.dinero} *Total: ${monedaDisplay}${carrito.total.toFixed(2)}*${bsTotal}`,
      sep,
      `${ICO.cliente} *Cliente:* ${form.cliente_nombre}`,
      form.cliente_telefono ? `${ICO.tel} *Telefono:* ${form.cliente_telefono}` : "",
      `${ICO.pin} *Entrega:* ${entregaTexto}`,
      `${ICO.tarjeta} *Pago:* ${pagoLabel}`,
      `${ICO.cal} *Fecha:* ${fecha}`,
      `${ICO.reloj} *Hora:* ${hora}`,
      notasLinea,
    ].filter((l) => l !== "").join("\n").replace(/\n{3,}/g, "\n\n").trim();
  };

  const enviarWhatsapp = (pedidoId) => {
    const wp = config?.whatsapp_config;
    const msg = construirMensaje(pedidoId);
    const telefono = wp?.telefono ? wp.telefono.replace(/\D/g, "") : "";
    const url = `https://wa.me/${telefono}?text=${encodeURIComponent(msg)}`;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const validarForm = () => {
    const errors = {};
    const nombre = form.cliente_nombre.trim();
    if (nombre.length < 2) errors.cliente_nombre = "El nombre debe tener al menos 2 caracteres";
    else if (nombre.length > 60) errors.cliente_nombre = "El nombre no puede superar 60 caracteres";
    const telefono = form.cliente_telefono.trim();
    if (monedaLocal === "VES") {
      const PREFIJOS_VE = ["0412", "0414", "0416", "0418", "0424", "0426"];
      if (telefono) {
        if (telefono.length !== 11) {
          errors.cliente_telefono = "El teléfono debe tener exactamente 11 dígitos (ej: 04121234567)";
        } else if (!PREFIJOS_VE.includes(telefono.slice(0, 4))) {
          errors.cliente_telefono = `Prefijo inválido. Válidos: ${PREFIJOS_VE.join(", ")}`;
        }
      }
    } else {
      if (telefono && telefono.length < 5) {
        errors.cliente_telefono = "Número de teléfono demasiado corto";
      }
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validarForm();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});
    if (metodosPago.length > 0 && !formaPago) { setError("Selecciona una forma de pago"); return; }
    setLoading(true); setError("");
    try {
      const pagoLabel = (metodosPago.find((m) => m.key === formaPago) || {}).label || formaPago;
      const notasParts = [pagoLabel ? `Pago: ${pagoLabel}` : "", tipoEntrega === "delivery" ? "" : "Retiro en tienda", form.notas].filter(Boolean);
      const payload = { cliente_nombre: form.cliente_nombre, cliente_telefono: form.cliente_telefono, cliente_direccion: tipoEntrega === "delivery" ? form.cliente_direccion : "Retiro en tienda", notas: notasParts.join(" | "), moneda: monedaLocal, items: carrito.items.map((i) => ({ producto_id: i.id, cantidad: i.qty })) };
      const { data } = await publicApi.post(`/menu/${subdominio}/pedido`, payload);
      enviarWhatsapp(data.id);
      onSuccess(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al procesar el pedido");
    } finally { setLoading(false); }
  };

  const inputStyle = { width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 12, padding: "11px 14px", fontSize: 14, outline: "none", boxSizing: "border-box", color: "#1e293b", transition: "border-color 0.15s", fontFamily: "inherit" };
  const focusColor = config?.color_tema || "#6366f1";

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 60, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", width: "100%", maxWidth: 480, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 -8px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ position: "sticky", top: 0, background: "#fff", padding: "20px 20px 14px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 1 }}>
          <h3 style={{ fontWeight: 800, fontSize: 18, color: "#0f172a", margin: 0 }}>Confirmar pedido</h3>
          <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "#f1f5f9", cursor: "pointer", fontSize: 20, color: "#64748b" }}>×</button>
        </div>
        <div style={{ padding: "16px 20px 32px" }}>
          <div style={{ background: "#f8fafc", borderRadius: 14, padding: 14, marginBottom: 20 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>Resumen del pedido</p>
            {carrito.items.map((i) => {
              const subtotal = Number(i.precio_promocion || i.precio) * i.qty;
              return (
                <div key={i.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: "#475569" }}>{i.qty}× {i.nombre}</span>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{monedaDisplay}{subtotal.toFixed(2)}</span>
                    {tasa && <span style={{ display: "block", fontSize: 10, color: "#94a3b8" }}>Bs. {(subtotal * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                  </div>
                </div>
              );
            })}
            <div style={{ borderTop: "1px dashed #e2e8f0", marginTop: 10, paddingTop: 10, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <span style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>Total</span>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>{monedaDisplay}{carrito.total.toFixed(2)}</span>
                {tasa && <span style={{ display: "block", fontSize: 11, color: "#64748b" }}>Bs. {(carrito.total * tasa).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
              </div>
            </div>
          </div>
          {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 10, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>{error}</div>}
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>Tipo de entrega</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[{ val: "delivery", icon: "🛵", label: "Delivery", sub: "Envío a domicilio" }, { val: "retiro", icon: "🏪", label: "Retiro", sub: "Paso a buscar" }].map(({ val, icon, label, sub }) => {
                  const active = tipoEntrega === val;
                  return (
                    <button key={val} type="button" onClick={() => setTipoEntrega(val)} style={{ padding: "12px 10px", borderRadius: 14, cursor: "pointer", textAlign: "center", border: active ? `2px solid ${focusColor}` : "2px solid #e2e8f0", background: active ? `${focusColor}10` : "#fff", transition: "all 0.15s" }}>
                      <div style={{ fontSize: 22, marginBottom: 2 }}>{icon}</div>
                      <div style={{ fontWeight: 800, fontSize: 13, color: active ? focusColor : "#374151" }}>{label}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>Tu nombre *</label>
              <input
                type="text"
                required
                value={form.cliente_nombre}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^a-záéíóúüñA-ZÁÉÍÓÚÜÑ\s]/g, "");
                  setForm({ ...form, cliente_nombre: val });
                  if (formErrors.cliente_nombre) setFormErrors({ ...formErrors, cliente_nombre: "" });
                }}
                placeholder="¿Cómo te llamamos?"
                maxLength={60}
                style={{ ...inputStyle, borderColor: formErrors.cliente_nombre ? "#ef4444" : "#e2e8f0" }}
                onFocus={e => e.target.style.borderColor = formErrors.cliente_nombre ? "#ef4444" : focusColor}
                onBlur={e => e.target.style.borderColor = formErrors.cliente_nombre ? "#ef4444" : "#e2e8f0"}
              />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                {formErrors.cliente_nombre
                  ? <span style={{ fontSize: 11, color: "#ef4444" }}>{formErrors.cliente_nombre}</span>
                  : <span />}
                <span style={{ fontSize: 11, color: form.cliente_nombre.length > 55 ? "#f59e0b" : "#94a3b8" }}>{form.cliente_nombre.length}/60</span>
              </div>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>WhatsApp / Teléfono</label>
              <input
                type="tel"
                value={form.cliente_telefono}
                onChange={(e) => {
                  const maxLen = monedaLocal === "VES" ? 11 : 20;
                  const val = e.target.value.replace(/[^\d+\-\s()]/g, "").slice(0, maxLen);
                  setForm({ ...form, cliente_telefono: val });
                  if (formErrors.cliente_telefono) setFormErrors({ ...formErrors, cliente_telefono: "" });
                }}
                placeholder={monedaLocal === "VES" ? "04121234567" : "Ej: +5712345678"}
                maxLength={20}
                style={{ ...inputStyle, borderColor: formErrors.cliente_telefono ? "#ef4444" : "#e2e8f0" }}
                onFocus={e => e.target.style.borderColor = formErrors.cliente_telefono ? "#ef4444" : focusColor}
                onBlur={e => e.target.style.borderColor = formErrors.cliente_telefono ? "#ef4444" : "#e2e8f0"}
              />
              {formErrors.cliente_telefono && <span style={{ fontSize: 11, color: "#ef4444", marginTop: 4, display: "block" }}>{formErrors.cliente_telefono}</span>}
            </div>
            {tipoEntrega === "delivery" && (
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>Dirección de entrega *</label>
                <input type="text" required={tipoEntrega === "delivery"} value={form.cliente_direccion} onChange={(e) => setForm({ ...form, cliente_direccion: e.target.value })} placeholder="Calle, edificio, referencia..." style={inputStyle} onFocus={e => e.target.style.borderColor = focusColor} onBlur={e => e.target.style.borderColor = "#e2e8f0"} />
              </div>
            )}
            {metodosPago.length > 0 && (
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>Forma de pago *</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {metodosPago.map(({ key, label }) => {
                    const active = formaPago === key;
                    return <button key={key} type="button" onClick={() => setFormaPago(key)} style={{ padding: "8px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer", border: active ? `2px solid ${focusColor}` : "1.5px solid #e2e8f0", background: active ? focusColor : "#fff", color: active ? "#fff" : "#374151", transition: "all 0.15s", boxShadow: active ? `0 2px 10px ${focusColor}44` : "none" }}>{label}</button>;
                  })}
                </div>
              </div>
            )}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>Observaciones</label>
              <textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder="Instrucciones especiales..." rows={2} style={{ ...inputStyle, resize: "none" }} onFocus={e => e.target.style.borderColor = focusColor} onBlur={e => e.target.style.borderColor = "#e2e8f0"} />
            </div>
            <button type="submit" disabled={loading} style={{ background: "#25d366", color: "#fff", border: "none", borderRadius: 14, padding: "15px 0", fontWeight: 800, fontSize: 15, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, boxShadow: "0 4px 20px rgba(37,211,102,0.35)", marginTop: 4 }}>
              {loading ? "Procesando..." : "Enviar pedido por WhatsApp"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ─── Página principal ────────────────────────────────────────────────────── */
export default function MenuPublico() {
  const { subdominio } = useParams();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [exito, setExito] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [catSeleccionada, setCatSeleccionada] = useState(null);
  const [tasasBCV, setTasasBCV] = useState(null);
  const carrito = useCarrito();

  const CACHE_KEY = `tc_splash_${subdominio}`;
  const splashInicial = (() => { try { return JSON.parse(localStorage.getItem(CACHE_KEY)); } catch { return null; } })();
  const [splashData, setSplashData] = useState(splashInicial);

  useEffect(() => {
    const minDelay = new Promise((r) => setTimeout(r, 1200));
    Promise.all([publicApi.get(`/menu/${subdominio}`), minDelay])
      .then(([res]) => {
        setMenu(res.data);
        const cfg = res.data?.config;
        if (cfg) {
          const nuevo = { logo_url: cfg.logo_url || null, color: cfg.color_tema || "#6366f1", nombre: res.data?.tienda?.nombre_comercio || "" };
          localStorage.setItem(CACHE_KEY, JSON.stringify(nuevo));
          setSplashData(nuevo);
          const nombreTienda = cfg.nombre_comercio || res.data?.tenant?.nombre_tienda || "";
          if (nombreTienda) document.title = nombreTienda;
          if (cfg.logo_url) {
            document.querySelectorAll("link[rel*='icon']").forEach((el) => el.remove());
            const link = document.createElement("link");
            link.rel = "icon";
            link.type = "image/png";
            link.href = `${BASE}${cfg.logo_url}`;
            document.head.appendChild(link);
          }
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    publicApi.get("/tasas/ultima").then((r) => setTasasBCV(r.data)).catch(() => {});
  }, [subdominio]);

  if (loading) {
    const bg = splashData?.color || "#6366f1";
    const logo = splashData?.logo_url ? `${BASE}${splashData.logo_url}` : null;
    const nombre = splashData?.nombre || "";
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: bg }}>
        <style>{`
          @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes logoPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.06); } }
          @keyframes bounce { 0%,80%,100% { transform: translateY(0); opacity: 0.5; } 40% { transform: translateY(-10px); opacity: 1; } }
        `}</style>
        <div style={{ textAlign: "center", animation: "fadeUp 0.4s ease" }}>
          {logo ? (
            <div style={{ width: 160, height: 160, borderRadius: "50%", overflow: "hidden", border: "4px solid rgba(255,255,255,0.9)", boxShadow: `0 0 0 3px ${bg}, 0 12px 40px rgba(0,0,0,0.25)`, margin: "0 auto 20px", background: "#fff", padding: 16, display: "flex", alignItems: "center", justifyContent: "center", animation: "logoPulse 2s ease-in-out infinite" }}>
              <img src={logo} alt={nombre} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </div>
          ) : (
            <div style={{ width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.2)", border: "4px solid rgba(255,255,255,0.8)", margin: "0 auto 20px", display: "flex", alignItems: "center", justifyContent: "center", animation: "logoPulse 2s ease-in-out infinite" }}>
              <span style={{ fontSize: 52, fontWeight: 900, color: "#fff" }}>{nombre ? nombre.charAt(0).toUpperCase() : "T"}</span>
            </div>
          )}
          {nombre && <p style={{ color: "#fff", fontWeight: 700, fontSize: 20, margin: "0 0 28px", textShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>{nombre}</p>}
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            {[0, 1, 2].map((i) => <div key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: "rgba(255,255,255,0.9)", animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />)}
          </div>
        </div>
      </div>
    );
  }

  if (error)
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", padding: 24 }}>
        <div style={{ textAlign: "center", maxWidth: 320 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>😕</div>
          <h1 style={{ fontWeight: 800, fontSize: 20, color: "#1e293b", margin: "0 0 8px" }}>Catálogo no encontrado</h1>
          <p style={{ color: "#94a3b8", fontSize: 14 }}>El enlace puede ser incorrecto o el catálogo no existe.</p>
        </div>
      </div>
    );

  if (exito)
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0fdf4", padding: 24 }}>
        <div style={{ textAlign: "center", maxWidth: 340 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
          <h1 style={{ fontWeight: 800, fontSize: 24, color: "#166534", margin: "0 0 10px" }}>¡Pedido confirmado!</h1>
          <p style={{ color: "#4ade80", fontSize: 15, marginBottom: 28 }}>Pronto el negocio se pondrá en contacto contigo.</p>
          <button onClick={() => { setExito(null); carrito.vaciar(); setBusqueda(""); setCatSeleccionada(null); }} style={{ background: "#22c55e", color: "#fff", border: "none", borderRadius: 14, padding: "14px 32px", fontWeight: 800, fontSize: 15, cursor: "pointer" }}>
            Seguir comprando
          </button>
        </div>
      </div>
    );

  const color = menu.config?.color_tema || "#6366f1";
  const monedaLocal = menu.tenant?.moneda_local || "USD";
  const monedaBCV = menu.config?.moneda_bcv;
  const tasa = monedaLocal === "VES" && monedaBCV
    ? (monedaBCV === "eur"    ? (tasasBCV?.eur || null)
     : monedaBCV === "usd"    ? (tasasBCV?.usd || null)
     : monedaBCV === "manual" ? (menu.config?.tasa_bcv || null)
     : null)
    : null;
  // Venezuela: precios almacenados en USD (o EUR), con Bs. como equivalencia secundaria
  const simbolo = monedaLocal === "VES"
    ? (monedaBCV === "eur" ? "€" : "$")
    : (SIMBOLOS_MONEDA[monedaLocal] || "$");

  const catsFiltradas = (menu.categorias || [])
    .filter((cat) => catSeleccionada === null || cat.id === catSeleccionada)
    .map((cat) => ({
      ...cat,
      productos: cat.productos.filter((p) =>
        !busqueda ||
        p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
        (p.descripcion || "").toLowerCase().includes(busqueda.toLowerCase())
      ),
    }))
    .filter((cat) => cat.productos.length > 0);

  const Plantilla = PLANTILLAS[menu.config?.plantilla] || PlantillaClasica;

  const props = {
    menu, color, tasa, simbolo, catsFiltradas, carrito,
    busqueda, setBusqueda, catSeleccionada, setCatSeleccionada,
    onOpenCart: () => setDrawerOpen(true),
  };

  const monedaLabel = monedaLocal !== "VES" ? (MONEDA_NOMBRES[monedaLocal] || null) : null;

  return (
    <>
      {menu.config?.fuente_nombre && (
        <link rel="stylesheet" href={FUENTES_URL} />
      )}
      <Plantilla {...props} monedaLabel={monedaLabel} />
      <CartDrawer open={drawerOpen} carrito={carrito} color={color} tasa={tasa} simbolo={simbolo} onClose={() => setDrawerOpen(false)} onCheckout={() => { setDrawerOpen(false); setCheckout(true); }} />
      <CheckoutModal open={checkout} carrito={carrito} subdominio={subdominio} config={menu.config} onClose={() => setCheckout(false)} onSuccess={(pedido) => { setCheckout(false); setExito(pedido); }} simbolo={simbolo} monedaLocal={monedaLocal} tasa={tasa} />
    </>
  );
}
